// Dummy questions store, shared between the question editors (and eventually
// a question bank list). Mirrors the pattern in questionnaires.js: a single
// reactive() store plus small helpers. Replace with backend-backed data once
// the API is wired up.
//
// Future endpoint shapes (following the existing apiService pattern, per
// SPEC_QUESTIONS_SCHEMA.md):
//
//   apiService.fetchQuestions(filters)        → GET    /api/seq/questions
//   apiService.fetchQuestion(id)              → GET    /api/seq/questions/:id
//   apiService.createQuestion(payload)        → POST   /api/seq/questions
//   apiService.updateQuestion(id, payload)    → PUT    /api/seq/questions/:id
//   apiService.archiveQuestion(id)            → POST   /api/seq/questions/:id/archive
//   apiService.restoreQuestion(id)            → POST   /api/seq/questions/:id/restore
//   apiService.fetchSections()                → GET    /api/seq/sections
//
// Schema reference (numeric_stepper content):
//   {
//     title:      { de: '...', en: '...', fr: '...', it: '...' },  // required (DE at minimum)
//     prompt:     i18n | null,    // optional
//     unit_label: i18n,           // required (DE at minimum), plural form
//     min:        int,
//     max:        int,
//     default:    int,  // min <= default <= max, min < max
//   }
// help_text is a separate top-level i18n object.
//
// All four locales are equal. The editor doesn't single any one out as
// required — title and unit_label just need at least one non-empty
// locale among the four at save time, in any combination.

import { reactive } from 'vue';

export const SUPPORTED_LOCALES = ['en', 'de', 'fr', 'it'];
// Which language tab opens by default. Page chrome is English so the
// editor lands on EN. Authors can fill in any subset of the four.
export const DEFAULT_LOCALE = 'en';
// Tab strip uses these labels. Page chrome is English so we keep the
// language names in English form (matches what the user typed: EN/DE/FR/IT).
export const LOCALE_LABELS = {
  en: 'EN',
  de: 'DE',
  fr: 'FR',
  it: 'IT',
};

// Sections are loaded from GET /api/seq/sections at mount time and stored
// on questionsState.sections. Each entry: {id, slug, name (i18n dict),
// position, questionnaire_id, questionnaire_slug, questionnaire_name,
// created_at, updated_at}. The 'Unassigned' option (section_id: null) is
// rendered separately by the editor / bank — it's not in this list.

// Locale fallback chain for displaying a section's i18n name. The page
// chrome is English, so EN comes first; then any other available locale.
export function localizeI18n(i18nObj, locale = 'en') {
  if (!i18nObj || typeof i18nObj !== 'object') return '';
  if (typeof i18nObj[locale] === 'string' && i18nObj[locale].trim()) {
    return i18nObj[locale].trim();
  }
  for (const loc of [locale, 'en', 'de', 'fr', 'it']) {
    const v = i18nObj[loc];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

const initialQuestions = [
  {
    id: 1,
    code: 'VEG_PORTIONS_DAY',
    section_id: 1,
    question_type: 'numeric_stepper',
    content: {
      title: {
        de: 'Wie viele Portionen Gemüse oder Salat isst du pro Tag?',
        en: 'How many portions of vegetables or salad do you eat per day?',
        fr: 'Combien de portions de légumes ou de salade manges-tu par jour ?',
        it: '', // IT intentionally empty to demo the "untranslated" indicator
      },
      prompt: {
        de: 'Tippe + oder − um deine Antwort anzupassen.',
        en: 'Tap + or − to adjust your answer.',
        fr: '',
        it: '',
      },
      unit_label: {
        de: 'Portionen',
        en: 'portions',
        fr: 'portions',
        it: '',
      },
      min: 0,
      max: 10,
      default: 2,
    },
    help_text: {
      de: 'Eine Portion entspricht etwa einer Handvoll oder 120 g.',
      en: 'One portion is about a handful, or 120 g.',
      fr: '',
      it: '',
    },
    status: 'active',
    created_at: '2026-04-12T09:30:00Z',
    updated_at: '2026-05-02T11:15:00Z',
    answer_count: 142,
  },
  {
    id: 2,
    code: 'WATER_GLASSES_DAY',
    section_id: 3,
    question_type: 'numeric_stepper',
    content: {
      title: {
        de: 'Wie viele Gläser Wasser trinkst du pro Tag?',
      },
      prompt: {
        de: '',
      },
      unit_label: {
        de: 'Gläser',
      },
      min: 0,
      max: 15,
      default: 4,
    },
    help_text: {},
    status: 'active',
    created_at: '2026-04-15T14:00:00Z',
    updated_at: '2026-04-15T14:00:00Z',
    answer_count: 0,
  },
  {
    id: 3,
    code: 'FRUIT_PORTIONS_OLD',
    section_id: 2,
    question_type: 'numeric_stepper',
    content: {
      title: {
        de: 'Frühere Version: Wie viele Portionen Obst isst du pro Tag?',
      },
      prompt: { de: '' },
      unit_label: { de: 'Portionen' },
      min: 0,
      max: 8,
      default: 2,
    },
    help_text: {},
    status: 'archived',
    created_at: '2025-11-20T08:00:00Z',
    updated_at: '2026-01-10T13:00:00Z',
    answer_count: 38,
  },
];

export const questionsState = reactive({
  items: initialQuestions.slice(),
  // Populated from GET /api/seq/sections on editor mount / bank mount.
  // Empty by default so a failed fetch doesn't show stale data.
  sections: [],
});

export function findQuestionById(id) {
  const numeric = Number(id);
  return questionsState.items.find(q => q.id === numeric) || null;
}

export function findSectionById(id) {
  if (id === null || id === undefined) return null;
  return questionsState.sections.find(s => s.id === id) || null;
}

// Returns true if the given code is unique across all questions, optionally
// ignoring one id (for the edit case where the question's own code is fine).
export function isCodeUnique(code, ignoreId = null) {
  if (!code) return false;
  return !questionsState.items.some(
    q => q.code === code && q.id !== ignoreId,
  );
}

export function addQuestion(payload) {
  const nextId = questionsState.items.reduce((max, q) => Math.max(max, q.id), 0) + 1;
  const now = new Date().toISOString();
  const item = {
    id: nextId,
    code: payload.code,
    section_id: payload.section_id ?? null,
    question_type: payload.question_type || 'numeric_stepper',
    content: deepClone(payload.content),
    help_text: deepClone(payload.help_text || {}),
    status: payload.status || 'active',
    created_at: now,
    updated_at: now,
    answer_count: 0,
  };
  questionsState.items.push(item);
  return item;
}

export function updateQuestion(id, patch) {
  const item = findQuestionById(id);
  if (!item) return null;
  if (patch.code !== undefined) item.code = patch.code;
  if (patch.section_id !== undefined) item.section_id = patch.section_id;
  if (patch.content !== undefined) item.content = deepClone(patch.content);
  if (patch.help_text !== undefined) item.help_text = deepClone(patch.help_text);
  if (patch.status !== undefined) item.status = patch.status;
  item.updated_at = new Date().toISOString();
  return item;
}

export function archiveQuestion(id) {
  return updateQuestion(id, { status: 'archived' });
}

export function restoreQuestion(id) {
  return updateQuestion(id, { status: 'active' });
}

// Code constraint from the schema (seq_questions_code_format CHECK):
//   ^[A-Z][A-Z0-9_]{0,31}$
// Uppercase letters, digits, underscores. Starts with a letter. 1–32 chars.
export const CODE_REGEX = /^[A-Z][A-Z0-9_]{0,31}$/;

// Used to seed a brand-new numeric_stepper question with sensible defaults.
// Matches the schema's content shape so the editor form can bind directly.
// All supported locales pre-populated with '' so v-model bindings have a
// reactive key to write to from the moment the form mounts.
export function blankStepperContent() {
  return {
    title: blankI18n(),
    prompt: blankI18n(),
    unit_label: blankI18n(),
    min: 0,
    max: 10,
    default: 2,
  };
}

export function blankI18n() {
  return SUPPORTED_LOCALES.reduce((acc, loc) => {
    acc[loc] = '';
    return acc;
  }, {});
}

// Normalize an i18n object loaded from the API (or the dummy store) so it
// always has all SUPPORTED_LOCALES as keys. Missing keys become ''. Used
// by the editor when binding v-model on tabs — every tab needs a key to
// write to. Non-destructive: returns a fresh object.
export function ensureAllLocales(i18nObj) {
  const out = blankI18n();
  if (i18nObj && typeof i18nObj === 'object') {
    for (const loc of SUPPORTED_LOCALES) {
      if (typeof i18nObj[loc] === 'string') out[loc] = i18nObj[loc];
    }
  }
  return out;
}

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}
